import { useCallback, useEffect, useRef, useState } from "react";
import { acquireSubscription, type SubscriptionHandle } from "@/features/chat/api/realtime";
import { useAuthStore } from "@/store/auth";
import { getIceServers } from "@/features/rtc/iceServers";
import { createRtcLogger, type RtcLogger } from "@/features/rtc/rtcLogger";
import { createSession, isSessionOpen, sendSignal } from "../api/screenshareApi";
import { useScreenShareUiStore } from "../store/screenshareStore";
import type {
  ScreenShareDirection,
  ScreenShareInbound,
  ScreenShareRole,
  ScreenShareUserBrief,
} from "../types";

export type ScreenSharePhase = "idle" | "calling" | "incoming" | "connecting" | "active";

interface IncomingRequest {
  sessionId: string;
  from: ScreenShareUserBrief;
  direction: ScreenShareDirection;
}

interface ActiveSession {
  id: string;
  role: ScreenShareRole;
}

// ICE "disconnected" is frequently transient (wifi handover, brief loss) —
// give it a grace window before forcing an ICE restart.
const DISCONNECTED_GRACE_MS = 7_000;
// An ICE restart that hasn't reconnected within this window is a failure.
const ICE_RESTART_DEADLINE_MS = 15_000;
// At most this many restarts within the sliding window below, then give up.
const ICE_RESTART_MAX = 2;
const ICE_RESTART_WINDOW_MS = 60_000;
// Phase watchdogs — neither "calling" nor "connecting" may hang forever.
const CALLING_TIMEOUT_MS = 90_000;
const CONNECTING_TIMEOUT_MS = 30_000;
// Backstop for an unanswered prompt. Normally a prompt is cleared by the other
// side (reject/end) or by the `session-closed` notice from another device of
// ours; this only covers the case where neither ever arrives (the caller's
// machine died mid-knock). Matches the caller's own "calling" watchdog.
const RING_TIMEOUT_MS = 90_000;

export interface UseScreenShare {
  phase: ScreenSharePhase;
  role: ScreenShareRole | null;
  peerName: string | null;
  peerId: string | null;
  /** Whether the peer is an internal GK user (has GG) — gates the 1:1 chat. */
  peerInternal: boolean;
  incoming: IncomingRequest | null;
  remoteStream: MediaStream | null;
  /** The peer is already in another session — shown while we keep waiting. */
  peerBusy: boolean;
  error: string | null;
  startViewRequest: (targetId: string) => Promise<void>;
  startShareOffer: (targetId: string) => Promise<void>;
  acceptIncoming: () => Promise<void>;
  rejectIncoming: () => void;
  stop: () => void;
  dismissError: () => void;
}

/**
 * Phases in which a knock cannot be answered here: a live session is running.
 * A prompt on screen ("incoming") is NOT busy — it is replaced by the new one.
 */
function busyPhase(phase: ScreenSharePhase): boolean {
  return phase !== "idle" && phase !== "incoming";
}

/**
 * Drives one screen-share session at a time. Mounted once (in ScreenShareHost).
 * Roles are fixed: the sharer is the WebRTC offerer (holds getDisplayMedia), the
 * viewer is the answerer. Signaling is relayed by the backend; this hook listens
 * on the user's own personal channel and POSTs outgoing signals.
 *
 * Resilience model (fixed roles, so no perfect negotiation needed here):
 * ICE "disconnected" starts a grace timer; on expiry (or on "failed") the
 * sharer re-offers with `iceRestart: true` — the viewer asks for that via the
 * "restart-request" signal. Restarts are capped; beyond the cap the session
 * tears down with an error. The Centrifugo `screenshare` namespace has
 * history+recovery, so handlers below tolerate replayed duplicates.
 */
export function useScreenShare(): UseScreenShare {
  const userId = useAuthStore((s) => s.user?.id) ?? null;

  const [phase, setPhaseState] = useState<ScreenSharePhase>("idle");
  const [role, setRole] = useState<ScreenShareRole | null>(null);
  const [peerName, setPeerName] = useState<string | null>(null);
  const [peerId, setPeerId] = useState<string | null>(null);
  const [peerInternal, setPeerInternal] = useState(false);
  const [incoming, setIncoming] = useState<IncomingRequest | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [peerBusy, setPeerBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Logic-critical state lives in refs so the once-attached publication handler
  // never reads a stale closure.
  const phaseRef = useRef<ScreenSharePhase>("idle");
  const sessionRef = useRef<ActiveSession | null>(null);
  const incomingRef = useRef<IncomingRequest | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteDescSetRef = useRef(false);
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  const lastOfferSdpRef = useRef<string | null>(null);
  const loggerRef = useRef<RtcLogger | null>(null);
  // Knocks settled while we were busy checking whether they were still live.
  // Publications interleave (the handler awaits), so an `end` can arrive between
  // our status check and its answer — without this we would ring for a session
  // we already know is over. Bounded: a tab only ever sees a handful.
  const settledRef = useRef<string[]>([]);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restartDeadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restartTimestampsRef = useRef<number[]>([]);

  const setPhase = useCallback((p: ScreenSharePhase) => {
    phaseRef.current = p;
    setPhaseState(p);
  }, []);

  const setIncomingBoth = useCallback((req: IncomingRequest | null) => {
    incomingRef.current = req;
    setIncoming(req);
  }, []);

  const ensureLogger = useCallback(
    (sessionId: string, peer: string | null): RtcLogger => {
      if (loggerRef.current) return loggerRef.current;
      const logger = createRtcLogger({
        kind: "screenshare",
        sessionId,
        peerId: peer ?? undefined,
      });
      loggerRef.current = logger;
      return logger;
    },
    [],
  );

  const clearRecoveryTimers = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
    if (restartDeadlineRef.current) {
      clearTimeout(restartDeadlineRef.current);
      restartDeadlineRef.current = null;
    }
  }, []);

  const teardown = useCallback(
    (initiator: string = "local-user") => {
      const logger = loggerRef.current;
      if (logger) {
        logger.log("teardown", { initiator });
        logger.dispose();
        loggerRef.current = null;
      }
      clearRecoveryTimers();
      restartTimestampsRef.current = [];
      lastOfferSdpRef.current = null;
      try {
        pcRef.current?.close();
      } catch {
        /* ignore */
      }
      pcRef.current = null;
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
      remoteDescSetRef.current = false;
      pendingIceRef.current = [];
      sessionRef.current = null;
      setRemoteStream(null);
      setRole(null);
      setPeerName(null);
      setPeerId(null);
      setPeerInternal(false);
      setPeerBusy(false);
      setIncomingBoth(null);
      setPhase("idle");
    },
    [clearRecoveryTimers, setIncomingBoth, setPhase],
  );

  const stop = useCallback(() => {
    const s = sessionRef.current;
    if (s) sendSignal(s.id, "end").catch(() => {});
    teardown("local-user");
  }, [teardown]);

  const flushPendingIce = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc) return;
    for (const cand of pendingIceRef.current) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(cand));
      } catch {
        /* ignore late/duplicate candidates */
      }
    }
    pendingIceRef.current = [];
  }, []);

  /**
   * Recover a degraded connection. The sharer (sole offerer) re-offers with
   * `iceRestart`; the viewer asks the sharer to do so. Capped to
   * ICE_RESTART_MAX per sliding window — beyond that the session ends.
   */
  const attemptIceRestart = useCallback(
    async (reason: string) => {
      const s = sessionRef.current;
      const pc = pcRef.current;
      if (!s || !pc) return;
      const now = Date.now();
      restartTimestampsRef.current = restartTimestampsRef.current.filter(
        (t) => now - t < ICE_RESTART_WINDOW_MS,
      );
      if (restartTimestampsRef.current.length >= ICE_RESTART_MAX) {
        loggerRef.current?.log("ice_restart_gave_up", { reason });
        setError("Połączenie zostało przerwane.");
        const sid = s.id;
        sendSignal(sid, "end").catch(() => {});
        teardown("ice-failed");
        return;
      }
      restartTimestampsRef.current.push(now);
      loggerRef.current?.log("ice_restart", { reason, role: s.role });

      if (restartDeadlineRef.current) clearTimeout(restartDeadlineRef.current);
      restartDeadlineRef.current = setTimeout(() => {
        restartDeadlineRef.current = null;
        const st = pcRef.current?.iceConnectionState;
        if (st && st !== "connected" && st !== "completed") {
          void attemptIceRestart("restart-deadline");
        }
      }, ICE_RESTART_DEADLINE_MS);

      if (s.role === "sharer") {
        try {
          const offer = await pc.createOffer({ iceRestart: true });
          await pc.setLocalDescription(offer);
          await sendSignal(s.id, "offer", offer);
        } catch {
          loggerRef.current?.log("ice_restart_offer_failed", { reason });
          setError("Połączenie zostało przerwane.");
          teardown("ice-failed");
        }
      } else {
        sendSignal(s.id, "restart-request").catch(() => {});
      }
    },
    [teardown],
  );

  const createPeer = useCallback(async (): Promise<RTCPeerConnection> => {
    const iceServers = await getIceServers();
    const pc = new RTCPeerConnection({ iceServers });
    loggerRef.current?.log("ice_config", {
      has_turn: iceServers.some((s) =>
        (Array.isArray(s.urls) ? s.urls : [s.urls]).some((u) =>
          String(u).startsWith("turn"),
        ),
      ),
    });
    loggerRef.current?.attachPc(pc);
    pc.onicecandidate = (e) => {
      const s = sessionRef.current;
      if (e.candidate && s) sendSignal(s.id, "ice", e.candidate.toJSON()).catch(() => {});
    };
    pc.ontrack = (e) => {
      // Viewer side — the shared screen arrives here.
      setRemoteStream(e.streams[0] ?? null);
      setPhase("active");
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      if (st === "connected" || st === "completed") {
        clearRecoveryTimers();
        setPhase("active");
      } else if (st === "disconnected") {
        // Transient more often than not — act only if it persists.
        if (!graceTimerRef.current) {
          graceTimerRef.current = setTimeout(() => {
            graceTimerRef.current = null;
            const cur = pcRef.current?.iceConnectionState;
            if (cur === "disconnected" || cur === "failed") {
              void attemptIceRestart("disconnected-grace");
            }
          }, DISCONNECTED_GRACE_MS);
        }
      } else if (st === "failed") {
        if (graceTimerRef.current) {
          clearTimeout(graceTimerRef.current);
          graceTimerRef.current = null;
        }
        void attemptIceRestart("failed");
      }
      // "closed" only ever follows our own teardown — nothing to do.
    };
    pcRef.current = pc;
    return pc;
  }, [attemptIceRestart, clearRecoveryTimers, setPhase]);

  /** Sharer: create + send the offer (media already captured, session set). */
  const sendOfferAsSharer = useCallback(async () => {
    const s = sessionRef.current;
    const stream = localStreamRef.current;
    if (!s || !stream) return;
    const pc = await createPeer();
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    setPhase("connecting");
    await sendSignal(s.id, "offer", offer);
  }, [createPeer, setPhase]);

  /** Capture the screen on a user gesture; ends the held stream if it stops. */
  const captureScreen = useCallback(async (): Promise<MediaStream> => {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
      // Chrome hides the CURRENT tab from the picker by default (anti "hall of
      // mirrors"). Sharing the ELGW5 tab you're on is the main case here, so
      // explicitly allow it to appear and be selectable.
      selfBrowserSurface: "include",
    } as DisplayMediaStreamOptions);
    localStreamRef.current = stream;
    // Native browser "Stop sharing" → tear the whole session down.
    stream.getVideoTracks()[0]?.addEventListener("ended", () => stop());
    return stream;
  }, [stop]);

  // ── Public actions ────────────────────────────────────────────────────────

  const startViewRequest = useCallback(
    async (targetId: string) => {
      if (phaseRef.current !== "idle") return;
      setError(null);
      setPeerBusy(false);
      try {
        const res = await createSession("view_request", targetId);
        sessionRef.current = { id: res.session_id, role: "viewer" };
        ensureLogger(res.session_id, res.peer.id).log("session_start", {
          role: "viewer",
          direction: "view_request",
        });
        setRole("viewer");
        setPeerName(res.peer.name);
        setPeerId(res.peer.id);
        setPeerInternal(res.peer.is_internal);
        setPhase("calling"); // wait for the sharer to accept + send an offer
      } catch {
        setError("Nie udało się wysłać prośby o podgląd.");
        teardown("local-error");
      }
    },
    [ensureLogger, setPhase, teardown],
  );

  const startShareOffer = useCallback(
    async (targetId: string) => {
      if (phaseRef.current !== "idle") return;
      setError(null);
      setPeerBusy(false);
      let stream: MediaStream;
      try {
        stream = await captureScreen(); // gesture: the picker click
      } catch {
        return; // user cancelled the native picker — nothing to clean up
      }
      try {
        const res = await createSession("share_offer", targetId);
        sessionRef.current = { id: res.session_id, role: "sharer" };
        ensureLogger(res.session_id, res.peer.id).log("session_start", {
          role: "sharer",
          direction: "share_offer",
        });
        setRole("sharer");
        setPeerName(res.peer.name);
        setPeerId(res.peer.id);
        setPeerInternal(res.peer.is_internal);
        setPhase("calling"); // wait for the viewer's `accept`, then offer
      } catch {
        stream.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
        setError("Nie udało się rozpocząć udostępniania.");
      }
    },
    [captureScreen, ensureLogger, setPhase],
  );

  /**
   * The knock we just accepted is already settled (409 from the relay —
   * answered on another device, withdrawn, or expired). Say so instead of
   * leaving the user on "Łączenie…" until the 30 s watchdog.
   */
  const setStaleSessionError = useCallback(() => {
    loggerRef.current?.log("accept_stale_session");
    setError("Ta sesja jest już nieaktualna — poproś o ponowne udostępnienie.");
    teardown("stale-session");
  }, [teardown]);

  const acceptIncoming = useCallback(async () => {
    const req = incomingRef.current;
    if (!req) return;
    setIncomingBoth(null);
    // We can be holding a session of our own: a knock that arrives while the
    // OS picker is open finds us "idle" (the session does not exist until the
    // user has picked a window). Answering must not orphan it — the peer would
    // sit on "Oczekiwanie na akceptację" until their 90 s watchdog.
    if (sessionRef.current && sessionRef.current.id !== req.sessionId) {
      stop();
    }
    if (req.direction === "view_request") {
      // I am the SHARER. This click is my gesture → capture + offer.
      sessionRef.current = { id: req.sessionId, role: "sharer" };
      ensureLogger(req.sessionId, req.from.id).log("session_start", {
        role: "sharer",
        direction: "view_request",
      });
      setRole("sharer");
      setPeerName(req.from.name);
      setPeerId(req.from.id);
      setPeerInternal(req.from.is_internal);
      try {
        await captureScreen();
      } catch {
        sendSignal(req.sessionId, "reject").catch(() => {});
        teardown("local-user");
        return;
      }
      setPhase("connecting");
      try {
        await sendOfferAsSharer();
      } catch {
        setStaleSessionError();
        return;
      }
    } else {
      // share_offer → I am the VIEWER. Tell the sharer to start offering.
      sessionRef.current = { id: req.sessionId, role: "viewer" };
      ensureLogger(req.sessionId, req.from.id).log("session_start", {
        role: "viewer",
        direction: "share_offer",
      });
      setRole("viewer");
      setPeerName(req.from.name);
      setPeerId(req.from.id);
      setPeerInternal(req.from.is_internal);
      setPhase("connecting");
      try {
        await sendSignal(req.sessionId, "accept");
      } catch {
        setStaleSessionError();
      }
    }
  }, [
    captureScreen,
    ensureLogger,
    sendOfferAsSharer,
    setIncomingBoth,
    setPhase,
    setStaleSessionError,
    stop,
    teardown,
  ]);

  const rejectIncoming = useCallback(() => {
    const req = incomingRef.current;
    if (!req) return;
    sendSignal(req.sessionId, "reject").catch(() => {});
    setIncomingBoth(null);
    setPhase("idle");
  }, [setIncomingBoth, setPhase]);

  // ── Inbound signaling ───────────────────────────────────────────────────

  const handleInbound = useCallback(
    async (msg: ScreenShareInbound) => {
      // A fresh knock. It reaches EVERY tab and every device of the recipient,
      // so what a tab must never do here is answer on the user's behalf: a
      // decline sent by a tab nobody is looking at kills the call for all the
      // others (2026-09-10 — ~20 stale prompts rejected every later request
      // within 50 ms). Ringing is the only reaction; ending is a human act.
      if (msg.type === "request") {
        // Centrifugo recovery can replay the knock for the session we're
        // already in (or already ringing about) — nothing to do either way.
        if (
          sessionRef.current?.id === msg.session_id ||
          incomingRef.current?.sessionId === msg.session_id ||
          // Already settled (or already checked and found dead) — recovery
          // replays the same knock on every reconnect; do not re-ask.
          settledRef.current.includes(msg.session_id)
        ) {
          return;
        }
        if (!msg.direction) return; // malformed — ignore it, quietly
        // Genuinely in a live session: tell the caller why nobody picks up.
        // Informational only — it leaves their session untouched.
        if (busyPhase(phaseRef.current)) {
          sendSignal(msg.session_id, "busy").catch(() => {});
          return;
        }
        // Ring only once the backend confirms the knock is still live. The
        // channel replays up to 120 s of history after a reconnect, so a tab
        // coming back from a websocket flap would otherwise ring for a ghost.
        const open = await isSessionOpen(msg.session_id);
        if (busyPhase(phaseRef.current)) {
          sendSignal(msg.session_id, "busy").catch(() => {});
          return;
        }
        if (!open) {
          // Remember the dead knock: recovery replays it on every reconnect,
          // and a flapping websocket would otherwise re-ask each time.
          settledRef.current = [...settledRef.current, msg.session_id].slice(-50);
          return;
        }
        if (
          settledRef.current.includes(msg.session_id) ||
          sessionRef.current?.id === msg.session_id
        ) {
          return;
        }
        // A newer knock replaces whatever prompt is on screen: the one that
        // just arrived is the one the user is being asked about.
        setIncomingBoth({
          sessionId: msg.session_id,
          from: msg.from,
          direction: msg.direction,
        });
        setPhase("incoming");
        return;
      }

      // A knock gets settled either by the other side (they gave up / declined)
      // or by another device of ours answering it — `session-closed` is the
      // backend telling our remaining tabs so. Remember it either way: a knock
      // whose status check is still in flight must not ring afterwards.
      if (
        msg.type === "end" ||
        msg.type === "reject" ||
        msg.type === "session-closed"
      ) {
        settledRef.current = [...settledRef.current, msg.session_id].slice(-50);
        if (incomingRef.current?.sessionId === msg.session_id) {
          setIncomingBoth(null);
          setPhase("idle");
          return;
        }
      }

      // Everything else must match the session we're in.
      const s = sessionRef.current;
      if (!s || s.id !== msg.session_id) return;

      switch (msg.type) {
        case "accept": {
          // Sharer (dir2) learns the viewer is ready → send the offer.
          // A replayed duplicate `accept` must not spawn a second pc.
          if (s.role === "sharer" && !pcRef.current) await sendOfferAsSharer();
          break;
        }
        case "offer": {
          // Viewer receives the sharer's offer (initial or ICE-restart) → answer.
          if (s.role !== "viewer") break;
          const sdp = (msg.payload as RTCSessionDescriptionInit)?.sdp ?? "";
          if (sdp && lastOfferSdpRef.current === sdp) break; // recovery replay
          lastOfferSdpRef.current = sdp || null;
          const pc = pcRef.current ?? (await createPeer());
          await pc.setRemoteDescription(
            new RTCSessionDescription(msg.payload as RTCSessionDescriptionInit),
          );
          remoteDescSetRef.current = true;
          await flushPendingIce();
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          await sendSignal(s.id, "answer", answer);
          break;
        }
        case "answer": {
          // Sharer receives the viewer's answer.
          const pc = pcRef.current;
          if (!pc) break;
          // Replayed duplicate while stable would throw — only apply when an
          // offer of ours is actually outstanding.
          if (pc.signalingState !== "have-local-offer") break;
          await pc.setRemoteDescription(
            new RTCSessionDescription(msg.payload as RTCSessionDescriptionInit),
          );
          remoteDescSetRef.current = true;
          await flushPendingIce();
          break;
        }
        case "ice": {
          const cand = msg.payload as RTCIceCandidateInit;
          if (!pcRef.current || !remoteDescSetRef.current) {
            pendingIceRef.current.push(cand);
          } else {
            try {
              await pcRef.current.addIceCandidate(new RTCIceCandidate(cand));
            } catch {
              /* ignore */
            }
          }
          break;
        }
        case "restart-request": {
          // Viewer's connection degraded — only the sharer can re-offer.
          if (s.role === "sharer") void attemptIceRestart("peer-request");
          break;
        }
        case "busy": {
          // The other side is in another session. Purely a hint for the human
          // at this end — the session keeps waiting until they cancel it.
          loggerRef.current?.log("peer_busy");
          setPeerBusy(true);
          break;
        }
        case "session-closed": {
          // Sent to every device of the user who settled the knock. Reaching
          // here means we ARE that device (a ringing tab returned above), so
          // the state it describes is already ours.
          break;
        }
        case "reject": {
          loggerRef.current?.log("peer_rejected");
          setError("Druga strona odrzuciła sesję.");
          teardown("remote-end");
          break;
        }
        case "end": {
          teardown("remote-end");
          break;
        }
      }
    },
    [
      attemptIceRestart,
      createPeer,
      flushPendingIce,
      sendOfferAsSharer,
      setIncomingBoth,
      setPhase,
      teardown,
    ],
  );

  // Backstop for a prompt nobody ever settles (the caller's machine died
  // mid-knock, so neither `end` nor `session-closed` will ever arrive).
  useEffect(() => {
    if (!incoming) return;
    const timer = setTimeout(() => {
      if (
        phaseRef.current !== "incoming" ||
        incomingRef.current?.sessionId !== incoming.sessionId
      ) {
        return;
      }
      loggerRef.current?.log("ring_timeout", { session_id: incoming.sessionId });
      setIncomingBoth(null);
      setPhase("idle");
    }, RING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [incoming, setIncomingBoth, setPhase]);

  // Keep the latest handler in a ref so the subscription effect runs once.
  const handlerRef = useRef(handleInbound);
  handlerRef.current = handleInbound;

  // Watchdogs: neither waiting for consent nor WebRTC negotiation may hang
  // forever (pre-fix behavior when a signal got lost in transit).
  useEffect(() => {
    if (phase !== "calling" && phase !== "connecting") return;
    const limit = phase === "calling" ? CALLING_TIMEOUT_MS : CONNECTING_TIMEOUT_MS;
    const timer = setTimeout(() => {
      if (phaseRef.current !== phase) return;
      loggerRef.current?.log("watchdog_timeout", { phase });
      setError(
        phase === "calling"
          ? "Brak odpowiedzi drugiej strony."
          : "Nie udało się nawiązać połączenia.",
      );
      const s = sessionRef.current;
      if (s) sendSignal(s.id, "end").catch(() => {});
      teardown("watchdog");
    }, limit);
    return () => clearTimeout(timer);
  }, [phase, teardown]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let handle: SubscriptionHandle | null = null;
    let pubHandler: ((ctx: { data: unknown }) => void) | null = null;

    (async () => {
      try {
        const h = await acquireSubscription(`screenshare:${userId}`);
        if (cancelled) {
          h.release();
          return;
        }
        handle = h;
        pubHandler = (ctx: { data: unknown }) => {
          handlerRef.current(ctx.data as ScreenShareInbound);
        };
        h.sub.on("publication", pubHandler);
      } catch {
        /* Centrifugo unavailable — screen share simply won't ring. */
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (pubHandler) handle?.sub.off("publication", pubHandler);
        handle?.release();
      } catch {
        /* ignore */
      }
    };
  }, [userId]);

  // Join the shared presence roster so this user shows up as "online" (and thus
  // reachable) in everyone's picker. No publications to handle — membership only.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let handle: SubscriptionHandle | null = null;
    (async () => {
      try {
        const h = await acquireSubscription("screenshare:presence");
        if (cancelled) {
          h.release();
          return;
        }
        handle = h;
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      try {
        handle?.release();
      } catch {
        /* ignore */
      }
    };
  }, [userId]);

  // Stop any live session if the host unmounts (logout / navigation away).
  useEffect(() => () => teardown("unmount"), [teardown]);

  // Expose `stop` so a logout (outside this subtree) can end the session while
  // still authenticated — the unmount teardown alone wouldn't notify the peer.
  const setEndActive = useScreenShareUiStore((s) => s.setEndActive);
  useEffect(() => {
    setEndActive(stop);
    return () => setEndActive(null);
  }, [stop, setEndActive]);

  // Hard stop on tab close / reload / navigation away (bfcache freeze too):
  // drop the capture immediately and best-effort tell the peer.
  useEffect(() => {
    const onPageHide = () => {
      const s = sessionRef.current;
      if (s) sendSignal(s.id, "end").catch(() => {});
      teardown("page-hide");
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [teardown]);

  return {
    phase,
    role,
    peerName,
    peerId,
    peerInternal,
    incoming,
    remoteStream,
    peerBusy,
    error,
    startViewRequest,
    startShareOffer,
    acceptIncoming,
    rejectIncoming,
    stop,
    dismissError: () => setError(null),
  };
}
